export const FEISHIN_DOWNLOADS_ROUTE = '/library/downloads';

export interface DownloadsSidebarItem {
    disabled: boolean;
    id: string;
    label: string;
    route: string;
}

export const withFeishinDownloadsItem = <T extends DownloadsSidebarItem>(
    items: T[],
    label: string,
    enabled: boolean,
): T[] => {
    if (!enabled || items.some((item) => item.id === 'Downloads')) return items;
    const next = [...items];
    const foldersIndex = next.findIndex((item) => item.id === 'Folders');
    next.splice(foldersIndex >= 0 ? foldersIndex + 1 : next.length, 0, {
        disabled: false,
        id: 'Downloads',
        label,
        route: FEISHIN_DOWNLOADS_ROUTE,
    } as T);
    return next;
};

export interface DownloadsIndicatorBatch {
    active_count?: number;
    created_at: string;
    files?: number;
    import_state?: string;
    imported?: boolean;
    imported_count?: number;
    last_activity_at?: null | string;
    stage: string;
}

export interface DownloadsIndicatorSummary<T> {
    /** Batches that really moved within the activity window. Drives the badge. */
    activeCount: number;
    /** Failed, partial or stuck batches: the red "needs attention" dot. */
    attentionCount: number;
    /** Finished batches the clean endpoint would hide. */
    cleanableCount: number;
    /** Newest first, capped for the popover. */
    recent: T[];
    /** Batches that claim to be live but have not moved in a long while. */
    stuckCount: number;
}

/**
 * How long a batch may sit without any transfer activity before it stops
 * counting as "in progress". musync can leave batches in downloading/importing
 * forever (a peer that vanished, a worker that died); without a window those
 * zombies kept the action bar icon animating permanently.
 */
export const DOWNLOAD_ACTIVITY_WINDOW_MS = 10 * 60 * 1000;

/** Stages musync never hides on clean: the batch still claims to be working. */
const LIVE_STAGES = new Set(['downloading', 'healing', 'importing']);
/** Stages `POST /acquire/feishin-downloads/clean` hides. */
const FINISHED_STAGES = new Set(['cancelled', 'complete', 'failed', 'partial']);
const FAILED_STAGES = new Set(['failed', 'partial']);
const RUNNING_IMPORT_STATES = new Set(['queued', 'running']);

/** The stage says work is happening, regardless of how long ago it last moved. */
export const isMovingStage = (batch: DownloadsIndicatorBatch): boolean => {
    if (batch.stage === 'downloading' || batch.stage === 'healing') return true;
    return batch.stage === 'importing' && RUNNING_IMPORT_STATES.has(batch.import_state ?? '');
};

/** Last moment the batch is known to have moved, as epoch ms (NaN if unknown). */
export const lastActivityMs = (batch: DownloadsIndicatorBatch): number =>
    Date.parse(batch.last_activity_at || batch.created_at);

/** Moved within the activity window. Future timestamps (clock skew) count as recent. */
export const isRecentlyActive = (
    batch: DownloadsIndicatorBatch,
    now: number,
    windowMs = DOWNLOAD_ACTIVITY_WINDOW_MS,
): boolean => {
    const last = lastActivityMs(batch);
    return Number.isFinite(last) && now - last <= windowMs;
};

/**
 * Really downloading, healing or importing right now.
 *
 * A running import is trusted as is: `last_activity_at` only tracks
 * transfers, which are all finished while the importer tags and files a large
 * album, so the window would call a working import stuck. musync's job lease
 * already turns a dead importer back into queued/failed, and musync's own
 * `poll_after_s` treats a running import as busy for the same reason.
 */
export const isBatchActive = (
    batch: DownloadsIndicatorBatch,
    now: number,
    windowMs = DOWNLOAD_ACTIVITY_WINDOW_MS,
): boolean => {
    if (!isMovingStage(batch)) return false;
    if (batch.stage === 'importing' && batch.import_state === 'running') return true;
    return isRecentlyActive(batch, now, windowMs);
};

/** Claims to be live but has not moved within the window (or cannot move). */
export const isBatchStuck = (
    batch: DownloadsIndicatorBatch,
    now: number,
    windowMs = DOWNLOAD_ACTIVITY_WINDOW_MS,
): boolean => LIVE_STAGES.has(batch.stage) && !isBatchActive(batch, now, windowMs);

export const isBatchCleanable = (batch: DownloadsIndicatorBatch): boolean =>
    FINISHED_STAGES.has(batch.stage);

/** Every file of the batch is already in the library. */
export const isBatchFullyImported = (batch: DownloadsIndicatorBatch): boolean =>
    batch.imported === true ||
    (Number(batch.files) > 0 && Number(batch.imported_count) >= Number(batch.files));

/**
 * `POST /acquire/batches/{id}/retry` only makes sense once nothing is in
 * flight and something is left to fetch: musync answers 409 "Batch is already
 * imported" when every file is in, which happens to batches marked failed only
 * because a post-import step of the import job failed.
 */
export const canRetryBatch = (batch: DownloadsIndicatorBatch): boolean =>
    FAILED_STAGES.has(batch.stage) &&
    !(batch.active_count && batch.active_count > 0) &&
    !isBatchFullyImported(batch);

export const summarizeDownloadsIndicator = <T extends DownloadsIndicatorBatch>(
    batches: T[],
    limit = 5,
    now = Date.now(),
): DownloadsIndicatorSummary<T> => {
    const recent = [...batches]
        .sort((a, b) => b.created_at.localeCompare(a.created_at))
        .slice(0, limit);
    const stuckCount = batches.filter((batch) => isBatchStuck(batch, now)).length;
    return {
        activeCount: batches.filter((batch) => isBatchActive(batch, now)).length,
        attentionCount:
            batches.filter((batch) => FAILED_STAGES.has(batch.stage)).length + stuckCount,
        cleanableCount: batches.filter(isBatchCleanable).length,
        recent,
        stuckCount,
    };
};

export type DownloadHealingNote =
    | { count: number; kind: 'missing' }
    | { count: number; kind: 'retried' }
    | { count: number; kind: 'retrying' }
    | { files: number; from: string; kind: 'replaced'; to: string }
    | { kind: 'tierDropped'; tier: null | string };

export interface DownloadHealingSummary {
    active: boolean;
    missing: number;
    replacements: Array<{ files: number; from: string; to: string }>;
    retries: number;
    tier: null | string;
    tier_dropped: boolean;
}

/**
 * Flatten musync's healing block into the short notes shown on a batch card,
 * in reading order: what is happening, what was swapped, what it cost.
 * Tolerates partial objects from older or newer servers.
 */
export const describeDownloadHealing = (
    healing: null | Partial<DownloadHealingSummary> | undefined,
): DownloadHealingNote[] => {
    if (!healing) return [];
    const notes: DownloadHealingNote[] = [];
    const retries = Math.max(0, Number(healing.retries) || 0);
    // An active heal with no retries yet is already said by the stage badge.
    if (retries > 0) notes.push({ count: retries, kind: healing.active ? 'retrying' : 'retried' });
    for (const replacement of healing.replacements || []) {
        if (!replacement?.from || !replacement?.to) continue;
        notes.push({
            files: Math.max(0, Number(replacement.files) || 0),
            from: replacement.from,
            kind: 'replaced',
            to: replacement.to,
        });
    }
    if (healing.tier_dropped) notes.push({ kind: 'tierDropped', tier: healing.tier || null });
    const missing = Math.max(0, Number(healing.missing) || 0);
    if (missing > 0) notes.push({ count: missing, kind: 'missing' });
    return notes;
};

export const downloadsPollIntervalMs = (pollAfterS: null | number | undefined): number => {
    const seconds = Number.isFinite(pollAfterS) ? Number(pollAfterS) : 15;
    return Math.min(30, Math.max(1, seconds)) * 1000;
};
